import React from 'react';
import { Smartphone, ExternalLink, Copy, CheckCircle, ChevronDown, ChevronUp, Phone } from 'lucide-react';

const CredentialRow = ({ label, value, fieldName, copiedField, onCopy, mask }) => (
  <div className="bg-gray-900 rounded-lg p-3">
    <div className="flex items-center justify-between">
      <div>
        <p className="text-gray-500 text-xs uppercase tracking-wide">{label}</p>
        <p className="text-white font-mono" data-testid={`sip-cred-${fieldName}`}>
          {mask ? '••••••••' : value}
        </p>
      </div>
      <button
        onClick={() => onCopy(value, fieldName)}
        className="p-2 text-gray-400 hover:text-white rounded-lg hover:bg-gray-700 transition-colors"
        title="Copy"
        data-testid={`copy-sip-${fieldName}-btn`}
      >
        {copiedField === fieldName ? <CheckCircle className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
      </button>
    </div>
  </div>
);

const ZoiperLinphoneInstructions = ({ t }) => (
  <div className="mt-3 text-gray-300 text-sm space-y-2">
    <p><strong>Zoiper Setup:</strong></p>
    <ol className="list-decimal list-inside space-y-1 text-gray-400">
      <li>{t('zoiperSetup.download')} <a href="https://www.zoiper.com" target="_blank" rel="noopener noreferrer" className="text-green-400 hover:underline">zoiper.com</a></li>
      <li>{t('zoiperSetup.openZoiper')}</li>
      <li>{t('zoiperSetup.enterCredentials')}</li>
      <li>{t('zoiperSetup.forProvider')} <code className="bg-gray-800 px-1 rounded">sip.telnyx.com</code></li>
      <li>{t('zoiperSetup.skipOptional')}</li>
      <li>{t('zoiperSetup.waitReady')}</li>
      <li>{t('zoiperSetup.keepRunning')}</li>
    </ol>
    <p className="mt-3"><strong>Linphone Setup:</strong></p>
    <ol className="list-decimal list-inside space-y-1 text-gray-400">
      <li>{t('linphoneSetup.download')} <a href="https://www.linphone.org" target="_blank" rel="noopener noreferrer" className="text-green-400 hover:underline">linphone.org</a></li>
      <li>{t('linphoneSetup.goToSettings')}</li>
      <li>{t('linphoneSetup.selectSIP')}</li>
      <li>{t('linphoneSetup.enterDetails')}</li>
      <li>{t('linphoneSetup.saveWait')}</li>
    </ol>
  </div>
);

export const SoftphoneSetupCard = ({ sipCredentials, copiedField, onCopy, showInstructions, onToggleInstructions, t }) => {
  if (!sipCredentials) return null;

  const username = sipCredentials.sip_username || sipCredentials.username;
  const password = sipCredentials.sip_password;

  return (
    <div className="mb-8">
      <div className="bg-gray-800 rounded-xl border border-gray-700 p-6" data-testid="softphone-setup-card">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-white flex items-center gap-2">
            <Smartphone className="w-5 h-5 text-green-400" />
            {t('userDashboard.softphoneSetup')}
          </h3>
          <div className="flex items-center gap-3">
            <span className="text-sm text-gray-400">
              {t('userDashboard.yourPhone')}:{' '}
              <span className="text-green-400 font-mono">
                {sipCredentials.telnyx_phone_number || t('userDashboard.notAssigned')}
              </span>
            </span>
            <a
              href="https://www.zoiper.com/en/voip-softphone/download/current"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 text-sm text-green-400 hover:text-green-300 bg-green-500/10 px-3 py-1.5 rounded-lg"
            >
              {t('userDashboard.downloadZoiper')} <ExternalLink className="w-3 h-3" />
            </a>
          </div>
        </div>

        <p className="text-gray-400 text-sm mb-4">{t('userDashboard.sipDescription')}</p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
          <CredentialRow label={t('userDashboard.sipServer')} value="sip.telnyx.com" fieldName="server" copiedField={copiedField} onCopy={onCopy} />
          <CredentialRow label={t('userDashboard.username')} value={username} fieldName="username" copiedField={copiedField} onCopy={onCopy} />
          <CredentialRow label={t('userDashboard.password')} value={password} fieldName="password" copiedField={copiedField} onCopy={onCopy} mask />
          <CredentialRow label={t('userDashboard.outboundProxy')} value="sip.telnyx.com:5060" fieldName="proxy" copiedField={copiedField} onCopy={onCopy} />
        </div>

        <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
          <button
            onClick={onToggleInstructions}
            className="flex items-center justify-between w-full text-left"
            data-testid="toggle-zoiper-instructions"
          >
            <span className="text-green-400 font-medium text-sm">{t('userDashboard.setupInstructions')}</span>
            {showInstructions ? <ChevronUp className="w-4 h-4 text-green-400" /> : <ChevronDown className="w-4 h-4 text-green-400" />}
          </button>
          {showInstructions && <ZoiperLinphoneInstructions t={t} />}
        </div>

        <div className="mt-4 p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
          <div className="flex items-center gap-2 text-sm">
            <Phone className="w-4 h-4 text-blue-400" />
            <span className="text-blue-400">{t('userDashboard.callsWillBeTransferred')}</span>
          </div>
        </div>
      </div>
    </div>
  );
};
